import { styled } from '@linaria/react';
import SmartBezierEdge from '@tisoap/react-flow-smart-edge';
import { memo, useContext } from 'react';
import { EdgeLabelRenderer, EdgeProps } from 'reactflow';
import i18n from '../../../base-dictionary';
import { useProcessStore } from '../../../store/process-store/process-store.hooks';
import { secondaryBase, skyDark } from '../../../styles/design-tokens';
import { ProcessContext } from '../../process/context/process.context';
import { resetProcessTaskDetails } from '../process-graph/process-graph.helpers';

export const EventEdge = memo(
  ({
    source,
    target,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    data,
    id,
    markerEnd,
    markerStart,
  }: EdgeProps) => {
    const edgeColor = data?.dashed ? skyDark : secondaryBase;

    // Scale weight for visual width with better distribution
    const safeWeight = data?.weight && data.weight > 1 ? data.weight : 1;
    const strokeWidth = Math.max(1.5, Math.min(Math.log(safeWeight) * 1.2, 6));

    // Custom edge style with the calculated color and width
    const edgeStyle = {
      stroke: edgeColor,
      strokeWidth,
      strokeLinecap: 'round' as const,
      strokeDasharray: data?.dashed ? '5,5' : 'none',
      ...style,
    };

    // We'll use this for the label position
    const centerX = (sourceX + targetX) / 2;
    const centerY = (sourceY + targetY) / 2;

    const smartEdgeOptions = {};

    return (
      <>
        <SmartBezierEdge
          id={id}
          source={source}
          target={target}
          sourceX={sourceX}
          sourceY={sourceY}
          targetX={targetX}
          targetY={targetY}
          sourcePosition={sourcePosition}
          targetPosition={targetPosition}
          style={edgeStyle}
          markerEnd={markerEnd}
          markerStart={markerStart}
          pathOptions={smartEdgeOptions}
        />

        {/* Display weight in the middle of the edge with styled label */}
        {data?.weight && (
          <EdgeLabelRenderer>
            <div
              style={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${centerX}px,${centerY}px)`,
                pointerEvents: 'all',
              }}
              className="nodrag nopan"
            >
              <EdgeLabel edgeId={`${source},${target}`} data={data} color={edgeColor} />
            </div>
          </EdgeLabelRenderer>
        )}
      </>
    );
  },
  (prev, next) => {
    // Custom comparison to prevent unnecessary rerenders
    return (
      prev.sourceX === next.sourceX &&
      prev.sourceY === next.sourceY &&
      prev.targetX === next.targetX &&
      prev.targetY === next.targetY &&
      prev.data?.weight === next.data?.weight &&
      prev.data?.percentage === next.data?.percentage &&
      prev.data?.cycle_time === next.data?.cycle_time &&
      prev.data?.cost === next.data?.cost &&
      prev.data?.viewType === next.data?.viewType &&
      prev.data?.dashed === next.data?.dashed &&
      prev.style?.stroke === next.style?.stroke &&
      prev.style?.strokeWidth === next.style?.strokeWidth
    );
  },
);

// Edge Label Component
export const EdgeLabel = ({
  edgeId,
  data,
  color,
}: {
  color: string;
  edgeId: string;
  data: {
    cost: number;
    cycle_time: number;
    dashed: boolean;
    isRightGraph: boolean;
    percentage: number;
    task_count: number;
    viewType: string;
    weight: number;
  };
}) => {
  const processContext = useContext(ProcessContext);
  const processStore = useProcessStore();
  const { eventScope, setEventScope, rightEventScope, setRightEventScope } = processStore;
  const getValue = () => {
    switch (data.viewType) {
      case 'percentage':
        return `${data.percentage}%`;
      case 'cycle_time':
        return `${data.cycle_time} days`;
      case 'cost':
        return `$${data.cost.toLocaleString()}`;
      default:
        return data.weight;
    }
  };

  const handleClick = () => {
    resetProcessTaskDetails(processContext, processStore);

    if (data.isRightGraph) {
      setRightEventScope({
        ...rightEventScope,
        selectedEdgeId: edgeId,
      });
      setEventScope({
        ...eventScope,
        selectedEdgeId: null,
      });
    } else {
      setEventScope({
        ...eventScope,
        selectedEdgeId: edgeId,
      });
      setRightEventScope({
        ...rightEventScope,
        selectedEdgeId: null,
      });
    }
  };
  return (
    <Label borderColor={color} bgColor={'white'} textColor={color} onClick={handleClick}>
      <Text>{getValue()}</Text>
      <Text>{`(${data.task_count} ${data.task_count > 1 ? i18n.t('common.work_units') : i18n.t('common.work_unit')})`}</Text>
    </Label>
  );
};

const Label = styled.div<{ borderColor: string; bgColor: string; textColor: string }>`
  min-width: 40px;
  height: 30px;
  padding: 0 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  border: 1.5px solid ${({ borderColor }) => borderColor};
  background: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Text = styled.div`
  font-family: Figtree, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 100%;
`;
